/**
 * Question Quality Engine — MeesterJay Governance Layer
 * REBALANCED SCORING MODEL (v2.1): 20+25+25+10+10+10 = 100pts
 *
 * PHASE 3 MIGRATION (Step 3.15): Removed direct legacy imports.
 * - tierAlignmentValidator → REMOVED, replaced with pipeline graph stage
 * - governanceValidator → REMOVED, replaced with pipeline
 * - Now uses runPipeline() as the single validation source
 *
 * ARCHITECTURE v4.0 — Phase 3, Steps 3.7 + 3.15
 */

import type { Question } from '../content';
import { GENERIC_EXPLANATION_PATTERNS, GENERIC_DISTRACTOR_PATTERNS, isGenericText, NON_MC_TYPES } from './utils';
import { assessExplanationDepth, assessDistractorDepth } from './didacticDepth';
import type { PipelineResult, PipelineBatchResult, ValidationIssue } from './pipeline/issueContract';
import { runPipeline } from './pipeline/orchestrator';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface QualityDimension {
  score: number;
  maxScore: number;
  issues: string[];
}

/**
 * Distractor issue — moved inline from questionQualityValidator (deprecated).
 * @see questionQualityValidator.ts — original source (scheduled for deletion)
 */
export interface DistractorIssue {
  questionId: string;
  familyId: string;
  tier: number;
  issue: string;
  severity: 'low' | 'medium' | 'high';
}

/**
 * Governance rule issue — canonical type for pipeline-extracted issues.
 */
export interface RuleIssue {
  severity: 'critical' | 'warning' | 'info';
  rule: string;
  message: string;
}

/**
 * Tier alignment result — defines the shape used by QuestionQualityScore.
 * Contains a normalized score and a list of issues from the graph stage.
 */
export interface TierAlignmentResult {
  score: number;
  issues: Array<{
    severity: 'critical' | 'warning' | 'info';
    message: string;
  }>;
}

// ─── analyzeDistractors — moved from questionQualityValidator (deprecated) ────

/**
 * Analyse distractor quality for a question.
 * Moved inline from questionQualityValidator.ts (deprecated).
 * Uses canonical GENERIC_DISTRACTOR_PATTERNS from governance/utils.ts.
 */
export function analyzeDistractors(question: Question): DistractorIssue[] {
  // BUG-6 fix: non-MC types skip distractor analysis
  if (NON_MC_TYPES.includes(question.questionType)) {
    return [];
  }
  const issues: DistractorIssue[] = [];
  const correctAnswer = question.options[question.correctAnswer];
  const distractors = question.options.filter((_, idx) => idx !== question.correctAnswer);
  for (let i = 0; i < distractors.length; i++) {
    const distractor = distractors[i];
    if (question.module !== 'SpellingWW' && question.module !== 'SpellingRegel' && question.module !== 'Taalbegrip') {
      const hasNumbers = /\d/.test(correctAnswer);
      const distractorHasNumbers = /\d/.test(distractor);
      if (hasNumbers && !distractorHasNumbers) {
        const isConceptualDistractor = /\b(even\s+groot|gelijk|niet\s+(te\s+)?(zeggen|bepalen|vergelijken)|geen\s+van|allebei|allemaal|onmogelijk|kan\s+niet|weet\s+niet)\b/i.test(distractor) || distractor.trim().length > 15 && !/\d/.test(distractor);
        const distractorIndex = question.options.indexOf(distractor);
        const hasExplanation = question.feedback?.distractorExplanations?.[distractorIndex.toString()];
        const explanationReferencesMisconception = typeof hasExplanation === 'string' && hasExplanation.trim().length > 20 && /fout|vergeten|verkeerd|verwisseld|verward|gedacht|misschien/i.test(hasExplanation);
        if (!isConceptualDistractor && !explanationReferencesMisconception) {
          issues.push({
            questionId: question.id,
            familyId: question.familyId,
            tier: question.tier,
            issue: `Distractor "${distractor}" lacks numbers while correct answer has numbers`,
            severity: 'high'
          });
        }
      }
    }
    if (question.tier >= 10) {
      const distractorLength = distractor.trim().length;
      const correctLength = correctAnswer.trim().length;
      if (distractorLength < correctLength / 3) {
        issues.push({
          questionId: question.id,
          familyId: question.familyId,
          tier: question.tier,
          issue: `Distractor "${distractor}" is suspiciously short for tier ${question.tier}`,
          severity: 'medium'
        });
      }
    }
    if (question.tier >= 7 && question.feedback?.distractorExplanations) {
      const distractorIndex = question.options.indexOf(distractor);
      const hasExplanation = question.feedback.distractorExplanations[distractorIndex.toString()];
      if (!hasExplanation || hasExplanation.trim() === '') {
        issues.push({
          questionId: question.id,
          familyId: question.familyId,
          tier: question.tier,
          issue: `Missing explanation for distractor "${distractor}"`,
          severity: 'low'
        });
      }
    }
    if (question.feedback?.distractorExplanations) {
      const distractorIndex = question.options.indexOf(distractor);
      const explanation = question.feedback.distractorExplanations[distractorIndex.toString()];
      if (explanation && explanation.trim().length > 0) {
        if (isGenericText(explanation, GENERIC_DISTRACTOR_PATTERNS)) {
          issues.push({
            questionId: question.id,
            familyId: question.familyId,
            tier: question.tier,
            issue: `Generic distractor explanation for "${distractor}" — must reference specific misconception (MASTER_2_3)`,
            severity: 'high'
          });
        }
      }
    }
  }
  return issues;
}
export interface QuestionQualityScore {
  questionId: string;
  familyId: string;
  module: string;
  tier: number;
  levelCode: string;
  group: number;
  totalScore: number;
  grade: 'excellent' | 'good' | 'needs-work' | 'poor';
  dimensions: {
    explanationClarity: QualityDimension;
    didacticDepth: QualityDimension;
    distractors: QualityDimension;
    metadata: QualityDimension;
    formatting: QualityDimension;
    tierAlignment: QualityDimension;
  };
  distractorAnalysis: DistractorIssue[];
  ruleIssues: RuleIssue[];
  tierAlignmentResult: TierAlignmentResult;
  flags: Array<{
    type: 'critical' | 'warning' | 'info';
    label: string;
  }>;
  explanationText?: string;
}
export interface ModuleQualitySummary {
  module: string;
  questionCount: number;
  avgScore: number;
  excellent: number;
  good: number;
  needsWork: number;
  poor: number;
  topIssues: Array<{
    issue: string;
    count: number;
  }>;
}

/** Canonical report type — use this, not QualityReport */
export interface QuestionQualityReport {
  generatedAt: string;
  totalQuestions: number;
  avgScore: number;
  distribution: {
    excellent: number;
    good: number;
    needsWork: number;
    poor: number;
  };
  scores: QuestionQualityScore[];
  byModule: ModuleQualitySummary[];
  topIssues: Array<{
    issue: string;
    count: number;
    severity: 'critical' | 'warning' | 'info';
  }>;
}

// NON_MC_TYPES imported from ./utils (single source of truth)

// ─── Scoring helpers ───────────────────────────────────────────────────────────

function gradeFromScore(score: number): QuestionQualityScore['grade'] {
  if (score >= 80) return 'excellent';
  if (score >= 60) return 'good';
  if (score >= 40) return 'needs-work';
  return 'poor';
}

// ─── Explanation Clarity (20pts) ──────────────────────────────────────────────

function scoreExplanationClarity(question: Question): QualityDimension {
  const issues: string[] = [];
  let score = 20;
  const explanationText = question.feedback?.explanationText?.trim() ?? '';
  if (!explanationText) {
    issues.push('Missing explanation text');
    score -= 20;
  } else {
    if (isGenericText(explanationText, GENERIC_EXPLANATION_PATTERNS)) {
      issues.push('Generic/boilerplate explanation — does not explain the concept or calculation');
      score -= 15;
    }
    if (explanationText.length < 30) {
      issues.push('Explanation too short (< 30 chars)');
      score -= 8;
    }
    if (explanationText.length > 600) {
      issues.push('Explanation very long (> 600 chars)');
      score -= 2;
    }
  }
  return {
    score: Math.max(0, score),
    maxScore: 20,
    issues
  };
}

// ─── Distractors (25pts) ───────────────────────────────────────────────────────

function scoreDistractors(question: Question): {
  dimension: QualityDimension;
  analysis: DistractorIssue[];
} {
  // BUG-6 fix: non-MC types cannot have distractorExplanations
  if (NON_MC_TYPES.includes(question.questionType)) {
    return {
      dimension: {
        score: 25,
        maxScore: 25,
        issues: []
      },
      analysis: []
    };
  }
  const issues: string[] = [];
  let score = 25;
  const options = question.options ?? [];
  const distractorExplanations = question.feedback?.distractorExplanations ?? {};
  const missingFeedback = options.filter((_, idx) => {
    const entry = distractorExplanations[String(idx)];
    return !entry || entry.trim().length === 0;
  });
  if (missingFeedback.length > 0) {
    issues.push(`Missing per-option feedback for ${missingFeedback.length} option(s)`);
    score -= missingFeedback.length * 5;
  }
  const genericDistractorCount = Object.values(distractorExplanations).filter(exp => typeof exp === 'string' && exp.trim().length > 0 && isGenericText(exp, GENERIC_DISTRACTOR_PATTERNS)).length;
  if (genericDistractorCount > 0) {
    issues.push(`${genericDistractorCount} generic distractor explanation(s) — must reference the specific misconception`);
    score -= genericDistractorCount * 4;
  }
  const uniqueOptions = new Set(options.map(o => o.trim().toLowerCase()));
  if (uniqueOptions.size < options.length) {
    issues.push('Duplicate answer options detected');
    score -= 8;
  }
  const emptyOptions = options.filter(o => !o || !o.trim());
  if (emptyOptions.length > 0) {
    issues.push(`${emptyOptions.length} empty option(s)`);
    score -= 8;
  }
  const analysis = analyzeDistractors(question);
  const highSeverity = analysis.filter(d => d.severity === 'high').length;
  const mediumSeverity = analysis.filter(d => d.severity === 'medium').length;
  score -= highSeverity * 4;
  score -= mediumSeverity * 2;
  if (highSeverity > 0) issues.push(`${highSeverity} implausible distractor(s) detected`);
  if (question.tier >= 5 && options.length >= 3) {
    const hasTrapDistractor = Object.values(distractorExplanations).some(exp => typeof exp === 'string' && (exp.toLowerCase().includes('fout') || exp.toLowerCase().includes('vergeten') || exp.toLowerCase().includes('verkeerd') || exp.toLowerCase().includes('misconceptie')));
    if (!hasTrapDistractor) {
      issues.push('No trap distractor explanation found (required for tier ≥ 5)');
      score -= 3;
    }
  }
  return {
    dimension: {
      score: Math.max(0, score),
      maxScore: 25,
      issues
    },
    analysis
  };
}

// ─── Metadata (10pts) ──────────────────────────────────────────────────────────

function scoreMetadata(question: Question): QualityDimension {
  const issues: string[] = [];
  let score = 10;
  if (!question.familyId?.trim()) {
    issues.push('Missing familyId');
    score -= 3;
  }
  if (!question.skill?.trim()) {
    issues.push('Missing skill');
    score -= 2;
  }
  if (!question.subSkill?.trim()) {
    issues.push('Missing subSkill');
    score -= 2;
  }
  if (!question.levelCode?.trim()) {
    issues.push('Missing levelCode');
    score -= 2;
  }
  if (!question.group || ![6, 7, 8].includes(question.group)) {
    issues.push(`Invalid group: ${question.group}`);
    score -= 1;
  }
  return {
    score: Math.max(0, score),
    maxScore: 10,
    issues
  };
}

// ─── Formatting (10pts) ────────────────────────────────────────────────────────

function scoreFormatting(question: Question): QualityDimension {
  const issues: string[] = [];
  let score = 10;
  const allText = [question.question, question.context ?? '', ...(question.options ?? []), question.feedback?.explanationText ?? ''].join(' ');
  const hasComma = /\d,\d/.test(allText);
  const hasPeriod = /\d\.\d/.test(allText);
  if (hasComma && hasPeriod) {
    issues.push('Inconsistent decimal separator (comma and period mixed)');
    score -= 4;
  }
  if (question.module === 'Getalbegrip') {
    const hasPlainFraction = /\b\d+\/\d+\b/.test(allText) && !allText.includes('\\frac');
    if (hasPlainFraction) {
      issues.push('Fractions should use LaTeX (\\frac{}{}) not plain /');
      score -= 3;
    }
  }
  if (question.question.length > 200) {
    issues.push(`Question text long (${question.question.length} chars, max ~200)`);
    score -= 2;
  }
  const latexMatches = allText.match(/\$[^$]+\$/g) ?? [];
  for (const match of latexMatches) {
    const open = (match.match(/{/g) ?? []).length;
    const close = (match.match(/}/g) ?? []).length;
    if (open !== close) {
      issues.push('Unmatched braces in LaTeX expression');
      score -= 4;
      break;
    }
  }
  return {
    score: Math.max(0, score),
    maxScore: 10,
    issues
  };
}

// ─── Tier Alignment (10pts) — Pipeline-based ──────────────────────────────────

/**
 * Extract tier alignment score from pipeline graph stage results.
 * Replaces direct validateTierAlignment() call.
 */
function scoreTierAlignmentFromPipeline(pipelineResult: PipelineResult): {
  dimension: QualityDimension;
  result: TierAlignmentResult;
} {
  const graphIssues = (pipelineResult.stageResults[2] ?? []).filter(i => i.code.includes('TIER') || i.code.includes('PREREQUISITE') || i.code.includes('GRAPH'));
  const issues: string[] = graphIssues.map(i => i.message);
  const tierIssues: TierAlignmentResult['issues'] = graphIssues.map(i => ({
    severity: i.kind === 'blocker' ? 'critical' as const : i.kind === 'coach' ? 'warning' as const : 'info' as const,
    message: i.message
  }));

  // Score: start at 10, deduct based on issue severity
  let score = 10;
  for (const issue of graphIssues) {
    if (issue.kind === 'blocker') score -= 5;else if (issue.kind === 'coach') score -= Math.round(issue.severity / 20);else score -= 1;
  }

  // Also use domain score as a cross-check
  const graphDomainScore = pipelineResult.domainScores.graph ?? 100;
  const normalizedScore = Math.round(graphDomainScore / 100 * 10);
  // Take the lower of the two for conservative scoring
  score = Math.min(Math.max(0, score), normalizedScore);
  return {
    dimension: {
      score: Math.max(0, score),
      maxScore: 10,
      issues
    },
    result: {
      score: Math.round(Math.max(0, score) / 10 * 15),
      // Back to 0-15 scale for compat
      issues: tierIssues
    }
  };
}

/**
 * Extract governance rule issues from pipeline results.
 * Replaces direct validateGovernance() call.
 */
function extractGovernanceIssues(pipelineResult: PipelineResult): RuleIssue[] {
  return pipelineResult.gate.issues.filter(i => i.domain === 'content' || i.domain === 'schema' || i.domain === 'graph').map(i => ({
    severity: i.kind === 'blocker' ? 'critical' as const : i.kind === 'coach' ? 'warning' as const : 'info' as const,
    rule: i.code,
    message: i.message
  }));
}

// ─── Main scorer ───────────────────────────────────────────────────────────────

/**
 * Score a single question.
 * @param question The question to score
 * @param _reserved Reserved parameter (unused, kept for call-site compat)
 * @param pipelineResult Optional pre-computed PipelineResult — when provided,
 * reuses pipeline data to avoid a second validation pass.
 */
export function scoreQuestion(question: Question, _reserved?: unknown, pipelineResult?: PipelineResult): QuestionQualityScore {
  // Ensure we have pipeline results (run if not provided)
  const pipeline = pipelineResult ?? runPipeline(question);
  const clarityDim = scoreExplanationClarity(question);
  const depthResult = assessExplanationDepth(question);
  const depthDim: QualityDimension = {
    score: depthResult.score,
    maxScore: depthResult.maxScore,
    issues: depthResult.issues.map(i => i.message)
  };
  const {
    dimension: distractorDim,
    analysis: distractorAnalysis
  } = scoreDistractors(question);
  const metadataDim = scoreMetadata(question);
  const formattingDim = scoreFormatting(question);

  // Tier alignment from pipeline (replaces validateTierAlignment)
  const {
    dimension: tierAlignmentDim,
    result: tierAlignmentResult
  } = scoreTierAlignmentFromPipeline(pipeline);
  const totalScore = Math.round(clarityDim.score + depthDim.score + distractorDim.score + metadataDim.score + formattingDim.score + tierAlignmentDim.score);

  // Governance issues from pipeline
  const ruleIssues: RuleIssue[] = extractGovernanceIssues(pipeline);
  const explanationText = question.feedback?.explanationText?.trim() ?? '';
  const flags: QuestionQualityScore['flags'] = [];
  if (!explanationText) flags.push({
    type: 'critical',
    label: 'No explanation'
  });
  if (explanationText && isGenericText(explanationText, GENERIC_EXPLANATION_PATTERNS)) flags.push({
    type: 'critical',
    label: 'Generic feedback'
  });
  if (ruleIssues.some(i => i.severity === 'critical')) flags.push({
    type: 'critical',
    label: 'Rule violation'
  });
  if (distractorAnalysis.some(d => d.severity === 'high')) flags.push({
    type: 'warning',
    label: 'Weak distractor'
  });
  const distractorExplanations = question.feedback?.distractorExplanations ?? {};
  const hasGenericDistractors = Object.values(distractorExplanations).some(exp => typeof exp === 'string' && exp.trim().length > 0 && isGenericText(exp, GENERIC_DISTRACTOR_PATTERNS));
  if (hasGenericDistractors) flags.push({
    type: 'warning',
    label: 'Generic distractor'
  });
  if (tierAlignmentResult.issues.some(i => i.severity === 'critical')) flags.push({
    type: 'critical',
    label: 'Tier mismatch'
  });else if (tierAlignmentResult.issues.some(i => i.severity === 'warning')) flags.push({
    type: 'warning',
    label: 'Tier drift'
  });
  if (totalScore >= 80) flags.push({
    type: 'info',
    label: 'High quality'
  });
  return {
    questionId: question.id,
    familyId: question.familyId,
    module: question.module,
    tier: question.tier,
    levelCode: question.levelCode,
    group: question.group,
    totalScore,
    grade: gradeFromScore(totalScore),
    dimensions: {
      explanationClarity: clarityDim,
      didacticDepth: depthDim,
      distractors: distractorDim,
      metadata: metadataDim,
      formatting: formattingDim,
      tierAlignment: tierAlignmentDim
    },
    distractorAnalysis,
    ruleIssues,
    tierAlignmentResult,
    flags,
    explanationText: explanationText || undefined
  };
}

// ─── Report builder ────────────────────────────────────────────────────────────

/**
 * Build a quality report for a set of questions.
 * @param pipelineBatchResult Optional pre-computed PipelineBatchResult — when provided,
 * reuses pipeline results per question to avoid a second validation pass.
 */
export function buildQualityReport(questions: Question[], pipelineBatchResult?: PipelineBatchResult): QuestionQualityReport {
  // Build lookup map from pre-computed pipeline results if available
  const pipelineResultMap = pipelineBatchResult ? new Map(pipelineBatchResult.results.map(r => [r.questionId, r])) : null;
  const scores = questions.map(q => scoreQuestion(q, undefined, pipelineResultMap?.get(q.id)));
  const distribution = {
    excellent: scores.filter(s => s.grade === 'excellent').length,
    good: scores.filter(s => s.grade === 'good').length,
    needsWork: scores.filter(s => s.grade === 'needs-work').length,
    poor: scores.filter(s => s.grade === 'poor').length
  };
  const avgScore = scores.length > 0 ? Math.round(scores.reduce((sum, s) => sum + s.totalScore, 0) / scores.length) : 0;
  const moduleMap = new Map<string, QuestionQualityScore[]>();
  scores.forEach(s => {
    const existing = moduleMap.get(s.module) ?? [];
    existing.push(s);
    moduleMap.set(s.module, existing);
  });
  const byModule: ModuleQualitySummary[] = Array.from(moduleMap.entries()).map(([module, moduleScores]) => {
    const issueMap = new Map<string, number>();
    moduleScores.forEach(s => {
      Object.values(s.dimensions).forEach(dim => {
        dim.issues.forEach(issue => {
          issueMap.set(issue, (issueMap.get(issue) ?? 0) + 1);
        });
      });
    });
    const topIssues = Array.from(issueMap.entries()).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([issue, count]) => ({
      issue,
      count
    }));
    return {
      module,
      questionCount: moduleScores.length,
      avgScore: Math.round(moduleScores.reduce((sum, s) => sum + s.totalScore, 0) / moduleScores.length),
      excellent: moduleScores.filter(s => s.grade === 'excellent').length,
      good: moduleScores.filter(s => s.grade === 'good').length,
      needsWork: moduleScores.filter(s => s.grade === 'needs-work').length,
      poor: moduleScores.filter(s => s.grade === 'poor').length,
      topIssues
    };
  }).sort((a, b) => a.avgScore - b.avgScore);
  const globalIssueMap = new Map<string, {
    count: number;
    severity: 'critical' | 'warning' | 'info';
  }>();
  scores.forEach(s => {
    s.ruleIssues.forEach(issue => {
      const existing = globalIssueMap.get(issue.message) ?? {
        count: 0,
        severity: issue.severity
      };
      globalIssueMap.set(issue.message, {
        count: existing.count + 1,
        severity: issue.severity
      });
    });
  });
  const topIssues = Array.from(globalIssueMap.entries()).sort((a, b) => b[1].count - a[1].count).slice(0, 10).map(([issue, {
    count,
    severity
  }]) => ({
    issue,
    count,
    severity
  }));
  return {
    generatedAt: new Date().toISOString(),
    totalQuestions: questions.length,
    avgScore,
    distribution,
    scores,
    byModule,
    topIssues
  };
}